import {
    Component,
    Input,
    inject,
    OnInit,
    signal,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    DestroyRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterModule } from '@angular/router'
import { AuthStateService } from '@baboor/baboor-auth'
import { AppDetailsService } from '@baboor/common-services'
import { BadgeModule } from '@openng/optimus-ui/badge'
import { TooltipModule } from '@openng/optimus-ui/tooltip'
import { clientNavigationItems, navigationItems } from './sidebar.data'
import { NavigationItem } from './sidebar.model'

@Component({
    selector: 'app-sidebar-default',
    imports: [RouterModule, BadgeModule, TooltipModule],
    templateUrl: './sidebar-default.component.html',
    styleUrl: './sidebar-default.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [AppDetailsService],
})
export class SidebarDefaultComponent implements OnInit {
    authStateService = inject(AuthStateService)
    private appDetailsService = inject(AppDetailsService)
    private cdr = inject(ChangeDetectorRef)
    private destroyRef = inject(DestroyRef)
    @Input() isCollapsed = false
    version = signal<string>('')

    filteredNavigationItems: NavigationItem[] = []
    filteredClientNavigationItems: NavigationItem[] = []
    isClient = signal(false)

    groupType = signal<string | null>(null)

    constructor() {
        this.updateNavigation()
    }

    async ngOnInit() {
        this.updateNavigation()

        this.authStateService
            .select('userDetails')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.updateNavigation()
            })

        const appVersion = await (
            await this.appDetailsService.getAppDetails()
        ).version
        this.version.set(appVersion)
    }

    private updateNavigation() {
        this.groupType.set(this.authStateService.getGroupType() ?? null)
        this.isClient.set(this.groupType() === 'client')
        this.filteredNavigationItems = this.filterItems(navigationItems)
        this.filteredClientNavigationItems = this.filterItems(
            clientNavigationItems,
        )
        this.cdr.markForCheck()
    }

    private filterItems(items: NavigationItem[]): NavigationItem[] {
        return items.filter(
            (item) =>
                // Super Admin-only items are hidden from every non-super-admin
                // navigation (vendor + client sidebars share this component).
                (!item.superAdminOnly ||
                    this.authStateService.isSuperAdmin()) &&
                (!item.permission ||
                    this.authStateService.hasPermission(item.permission)),
        )
    }
}
